import { Box, Flex, HStack, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import PageCard from 'components/page-card';
import EmptyState from 'components/states/EmptyState';
import ErrorState from 'components/states/ErrorState';
import SortIndicator from 'components/table/SortIndicator';

import { useNavigate } from 'react-router-dom';
import { MdSportsTennis } from 'react-icons/md';
import { CourtIllustration } from 'components/illustrations';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import ScopePrompt from '../../_shared/ScopePrompt';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetCourts } from '../hooks/useGetCourts';
import CourtActionMenu from './CourtActionMenu';
import { useIsOrgManager } from '../../auth/roles';
import CreateCourtForm from './CreateCourtForm';
import CreateCourtGridForm from '../../court-pricing-grid/components/CreateCourtGridForm';

const columnHelper = createColumnHelper<CourtDto>();

export default function CourtTable() {
    const isOrgManager = useIsOrgManager();
    const { selectedLocation } = useSelectedLocation();
    const { data, isFetching, isError, state } = useGetCourts(selectedLocation?.id);

    const [sorting, setSorting] = useState<SortingState>([]);
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const navigation = useNavigate();

    const columns = useMemo(() => [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    ID
                </Text>
            ),
            cell: (info) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('courts.title')}
                </Text>
            ),
            cell: (info) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.getValue()}
                    </Text>
                </Flex>
            )
        })
    ], [textColor]);
    const table = useReactTable({
        data: data || [],
        columns,
        state: {
            sorting
        },
        initialState: {
            columnVisibility: {
                id: false
            }
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <PageCard title={translate('tabs.courts')} rightHeader={isOrgManager ? (
            // Both forms stay side by side while the grid-based one is evaluated
            // against real schedules (see docs/epics/court-pricing-grid.md).
            <HStack spacing={3}>
                <CreateCourtGridForm />
                <CreateCourtForm />
            </HStack>
        ) : null}>
            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                                {/* Coaches get no row actions, so the column
                                    itself is dropped rather than rendered
                                    empty (it must match the Td below). */}
                                {isOrgManager && <Th key={'actions'}
                                    width={10}
                                    {...tableThProps}
                                    borderColor={borderColor}
                                    cursor='pointer'>

                                </Th>}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr key={row.id}
                                    {...tableRowProps}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                onClick={() => {
                                                    navigation(`/admin/courtDetails?court=${row.getValue('id')}`);
                                                }}
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                    {isOrgManager && <Td>
                                        <CourtActionMenu courtId={row.getValue('id')} />
                                    </Td>}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                {isError && (
                    <ErrorState message={translate('error.loading' as any)} />
                )}
                {/*
                    `state === 'answered'` rather than `!isLoading && !isFetching
                    && !isError` (R110-6 / R133-4). This is the sharpest instance
                    of the class in the whole `useGetCourts` population: the
                    empty state here is the FIRST-USE one — an illustration and
                    "create your first court" — and before run 134 gated the hook
                    on `locationId`, an operator at a multi-location org who had
                    simply not picked a club got it on the page whose entire job
                    is to list courts. The read really was issued
                    (`courts?locationId=undefined`) and really did answer 200
                    with `[]`, so every flag in the old conjunct was correct and
                    the sentence it guarded was still false.
                */}
                {state === 'answered' && data?.length === 0 && (
                    <EmptyState
                        variant="first-use"
                        illustration={<CourtIllustration />}
                        title={translate('walkthrough.empty_courts_title')}
                        description={translate('walkthrough.empty_courts_description')}
                    />
                )}
                {/*
                    Never ISSUED: no club is selected, so the honest render is a
                    prompt for the thing that is missing — not a first-use empty
                    state, and not the permanent spinner the raw `isLoading`
                    below would produce for a disabled query (D64).
                */}
                {state === 'never-issued' && <ScopePrompt gate="location" />}
                <Loader isLoading={state === 'loading' || (isFetching && !data?.length)} />
            </Box>
        </PageCard>
    );
}